import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@b2b/ui-components";
import { chatService } from "@b2b/services";
import type {
  AdminConversationListItem,
  AdminPaginatedConversations,
  ChatMessage,
  ChatCounterpart,
} from "@b2b/services/api/modules/chat/chat.types";

const formatName = (c: ChatCounterpart) =>
  [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || "—";

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const truncate = (s: string, max: number) =>
  s.length <= max ? s : s.slice(0, max) + "…";

export default function AdminChatMonitoring() {
  const [conversations, setConversations] = useState<AdminConversationListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const params: Parameters<typeof chatService.listAdminConversations>[0] = {
        page,
        limit,
      };
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      const data: AdminPaginatedConversations = await chatService.listAdminConversations(params);
      setConversations(data.items);
      setTotal(data.total);
    } catch (err: any) {
      const msg = err?.response?.data?.message || "Failed to load conversations";
      setListError(msg);
      showToast("error", msg);
    } finally {
      setLoading(false);
    }
  }, [page, limit, fromDate, toDate]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const fetchMessages = useCallback(
    async (conversationId: string, cursor?: string) => {
      setMessagesLoading(true);
      setMessagesError(null);
      try {
        const data = await chatService.listAdminMessages(conversationId, {
          cursor,
          limit: 50,
        });
        const newItems = data.items;
        if (!cursor) {
          setMessages(newItems);
        } else {
          setMessages((prev) => [...prev, ...newItems]);
        }
        setNextCursor(data.nextCursor);
      } catch (err: any) {
        const msg = err?.response?.data?.message || "Failed to load messages";
        setMessagesError(msg);
        showToast("error", msg);
        setMessages([]);
      } finally {
        setMessagesLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      setNextCursor(null);
      setAttachmentUrls({});
      return;
    }
    fetchMessages(selectedId);
  }, [selectedId, fetchMessages]);

  const ensureAttachmentUrls = useCallback(async (keys: string[]) => {
    if (keys.length === 0) return;
    const updates: Record<string, string> = {};
    await Promise.all(
      keys.map(async (k) => {
        if (!k) return;
        try {
          const res = await chatService.getAdminAttachmentUrl(k);
          updates[k] = res.url;
        } catch (e) {
          console.error("Failed to fetch admin attachment URL for", k, e);
        }
      })
    );
    setAttachmentUrls((prev) => ({ ...prev, ...updates }));
  }, []);

  useEffect(() => {
    const keys = messages.flatMap((m) => (m.attachments || []) as string[]);
    const unique = [...new Set(keys)].filter(Boolean);
    const missing = unique.filter((k) => !attachmentUrls[k]);
    if (missing.length > 0) ensureAttachmentUrls(missing);
  }, [messages, attachmentUrls, ensureAttachmentUrls]);

  const selectedConversation = conversations.find((c) => c.id === selectedId);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const tableData = conversations.map((c) => ({
    id: c.id,
    buyer: formatName(c.buyer),
    buyerEmail: c.buyer.email,
    seller: formatName(c.seller),
    sellerEmail: c.seller.email,
    lastMessageAt: formatDate(c.lastMessageAt),
    lastMessagePreview: truncate(c.lastMessage?.message || "—", 40),
    actions: (
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setSelectedId(c.id);
          setIsChatDialogOpen(true);
        }}
      >
        View
      </Button>
    ),
  }));

  return (
    <main className="p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Chat Monitoring</h1>
        <p className="text-sm text-gray-500">View all buyer-seller conversations (read-only)</p>
      </div>

      {toast && (
        <div
          className={`rounded px-4 py-2 text-sm ${
            toast.type === "error" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"
          }`}
        >
          {toast.message}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Conversations ({total})</CardTitle>
          <div className="flex flex-wrap gap-2 mt-2">
            <Input
              type="date"
              placeholder="From"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="max-w-[140px]"
            />
            <Input
              type="date"
              placeholder="To"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="max-w-[140px]"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchConversations()}
              disabled={!fromDate || !toDate}
            >
              Apply
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {listError && (
            <div className="text-sm text-red-600 mb-2">{listError}</div>
          )}
          {loading ? (
            <div className="text-sm text-gray-500 py-4">Loading...</div>
          ) : conversations.length === 0 ? (
            <div className="text-sm text-gray-500 py-4">No conversations found</div>
          ) : (
            <>
              <DataTable
                data={tableData}
                config={{
                  columns: [
                    { key: "buyer", label: "Buyer" },
                    { key: "seller", label: "Seller" },
                    { key: "lastMessageAt", label: "Last message" },
                    { key: "lastMessagePreview", label: "Preview" },
                    { key: "actions", label: "Actions" },
                  ],
                  styles: {
                    headerBg: "bg-gray-100",
                    headerText: "text-gray-700",
                    rowHover: "hover:bg-gray-50",
                  },
                }}
                rowsPerPage={limit}
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={isChatDialogOpen} onOpenChange={setIsChatDialogOpen}>
        <DialogContent className="sm:max-w-4xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">
              {selectedConversation
                ? `${formatName(selectedConversation.buyer)} ↔ ${formatName(selectedConversation.seller)}`
                : "Conversation"}
            </DialogTitle>
            {selectedConversation && (
              <div className="text-xs text-gray-500">
                Buyer: {selectedConversation.buyer.email} · Seller: {selectedConversation.seller.email}
              </div>
            )}
          </DialogHeader>
          {messagesError && (
            <div className="text-sm text-red-600 mb-2">{messagesError}</div>
          )}
          <div className="h-[560px] overflow-y-auto flex flex-col gap-3 border rounded-lg p-3 bg-gray-50">
            {messagesLoading && !messages.length ? (
              <div className="text-sm text-gray-500">Loading messages...</div>
            ) : (
              <>
                {nextCursor && selectedId && (
                  <div className="flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={messagesLoading}
                      onClick={() => fetchMessages(selectedId, nextCursor)}
                    >
                      {messagesLoading ? "Loading..." : "Load older"}
                    </Button>
                  </div>
                )}
                {[...messages].reverse().map((m) => {
                  const isBuyer = selectedConversation && m.senderId === selectedConversation.buyerId;
                  const senderName = isBuyer
                    ? formatName(selectedConversation!.buyer)
                    : selectedConversation
                      ? formatName(selectedConversation.seller)
                      : "—";
                  const hasAttachments = (m.attachments || []).length > 0;
                  const normalizedMessage = (m.message || "").trim().toLowerCase();
                  const shouldHideAttachmentPlaceholder =
                    hasAttachments &&
                    (normalizedMessage === "attachment" || normalizedMessage === "[attachment]");
                  const displayMessage = shouldHideAttachmentPlaceholder ? "" : (m.message || "");
                  return (
                    <div
                      key={m.id}
                      className={`flex ${isBuyer ? "justify-start" : "justify-end"}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg p-3 text-sm ${
                          isBuyer ? "bg-blue-100 text-gray-900" : "bg-orange-100 text-gray-900"
                        }`}
                      >
                        <div className="text-xs font-medium text-gray-600 mb-1">
                          {senderName} {isBuyer ? "(Buyer)" : "(Seller)"}
                        </div>
                        {displayMessage ? <div>{displayMessage}</div> : null}
                        {hasAttachments && (
                          <div className="mt-2 space-y-2">
                            {(m.attachments || []).map((k: string) => {
                              const url = attachmentUrls[k];
                              return url ? (
                                <a
                                  key={k}
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block"
                                >
                                  <img
                                    src={url}
                                    alt="attachment"
                                    className="max-h-48 rounded border"
                                  />
                                </a>
                              ) : (
                                <div key={k} className="text-xs text-gray-500">
                                  Loading image...
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div className="mt-1 text-[10px] text-gray-500">
                          {formatDate(m.createdAt)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && !messagesLoading && (
                  <div className="text-sm text-gray-500 text-center py-4">
                    No messages in this conversation
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
